/* eslint-disable class-methods-use-this */
import { UniqueIdentifier } from '@dnd-kit/core';
import { arrayMove, arraySwap } from '@dnd-kit/sortable';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import {
    DemeterDashboardModel,
    DemeterDashboardWidgetLayoutType,
    DemeterDashboardWidgetModel,
    DemeterDashboardWidgetPageElementModel,
    DemeterMarket,
    DemeterPageElementModel,
    DemeterPageElementType,
    DemeterPageType,
} from '../../../Generated/Raven-Demeter';
import { ComponentHeaderRightSideOptions } from '../../Components/Form/Inputs/ComponentMoreOptionsHorizontalDots';
import AddWidgetCustomization from './AddWidgetCustomization';
import { pageElementsConstraintsAndInitializations } from './DashboardCustomizationDefinitions';
import GenerateComponentFromPageElement from './GenerateComponentFromPageElement';

export interface IDragAndDropDashboard extends DemeterDashboardModel {
    dragAndDropRows: IDragAndDropRow[];
}

export interface IDragAndDropRow extends DemeterDashboardWidgetModel {
    dragAndDropRowId: string;
    stableRowId: string;
    isRowDivider: boolean;
    widgets: IDragAndDropWidget[];
}

export interface IDragAndDropWidget extends DemeterDashboardWidgetPageElementModel {
    // dnd-kit requires id to be of type UniqueIdentifier for each widget and expects the key to be named 'id'
    id: UniqueIdentifier;
    stableWidgetId: UniqueIdentifier;
    component: React.ReactNode;
    isAddWidget: boolean;
}

class DashboardCustomizationService {
    private stableIdCounter = 0;

    private generateStableId = (prefix: string): string => {
        this.stableIdCounter += 1;
        return `${prefix}-stable-${this.stableIdCounter}-${Date.now()}`;
    };

    createDashboardDeepCopy = (dashboard: DemeterDashboardModel): DemeterDashboardModel => ({
        ...dashboard,
        rows: dashboard.rows.map((row) => ({
            ...row,
            rows: row.rows.map((widget) => ({ ...widget })),
        })),
    });

    getNewEmptyDashboard = (name: string, dashboardOrder: number = 0) => ({
        demeterDashboardGuid: applicationConstants.ZeroGuid,
        name: `${name} ${!dashboardOrder ? '' : dashboardOrder}`,
        order: dashboardOrder,
        rows: [],
    });

    getNewDefaultDashboard = (
        name: string,
        pageElements: DemeterPageElementModel[],
        markets: DemeterMarket[],
        dashboardOrder: number = 0,
        currentDemeterDashboardGuid?: string,
    ): DemeterDashboardModel => {
        const filteredPageElementsConstraintsAndInitializations = pageElementsConstraintsAndInitializations.filter((x) => {
            if (
                x.pageElementType === DemeterPageElementType.CommodityIndicators &&
                pageElements.some((y) => y.pageElementType === DemeterPageElementType.HedgeMonitorPro)
            ) {
                return false;
            }

            if (x.DefaultDashboardMarkets && !x.DefaultDashboardMarkets.some((market) => markets.includes(market))) {
                return false;
            }

            if (x.restrictToDairy && markets.some((market) => market !== DemeterMarket.Dairy)) {
                return false;
            }

            return true;
        });

        const defaultDashboardRows: DemeterDashboardWidgetModel[] = filteredPageElementsConstraintsAndInitializations
            .reduce((dashboardRows, pageElementConfiguration) => {
                const rowOrder = pageElementConfiguration.rowOrder || 0;
                const matchingPageElement = pageElements.find((pageElement) => pageElement.pageElementType === pageElementConfiguration.pageElementType);

                if (!matchingPageElement) {
                    return dashboardRows;
                }

                const existingRowIndex = dashboardRows.findIndex((row) => row.order === rowOrder);
                if (existingRowIndex === -1) {
                    const newRow: DemeterDashboardWidgetModel = {
                        name: '',
                        order: rowOrder,
                        width: 0,
                        height: 0,
                        positionX: 0,
                        positionY: 0,
                        layoutType: pageElementConfiguration.layoutType ?? pageElementConfiguration.initialLayoutType ?? undefined,
                        options: {},
                        rows: [
                            {
                                demeterDashboardWidgetPageElementGuid: applicationConstants.ZeroGuid,
                                order: 0,
                                options: {},
                                pageElement: matchingPageElement,
                            },
                        ],
                    };
                    dashboardRows.push(newRow);
                } else {
                    dashboardRows[existingRowIndex].rows.push({
                        demeterDashboardWidgetPageElementGuid: applicationConstants.ZeroGuid,
                        order: dashboardRows[existingRowIndex].rows.length,
                        options: {},
                        pageElement: matchingPageElement,
                    });
                }

                return dashboardRows;
            }, [] as DemeterDashboardWidgetModel[])
            .sort((a, b) => a.order - b.order)
            .map((row, index) => ({ ...row, order: index }));

        return {
            demeterDashboardGuid: currentDemeterDashboardGuid ?? applicationConstants.ZeroGuid,
            name: `${name} ${!dashboardOrder ? '' : dashboardOrder}`,
            order: dashboardOrder,
            rows: defaultDashboardRows,
        };
    };

    convertDragAndDropRowsToDashboardModel = (dragAndDropRows: IDragAndDropRow[], demeterDashboard: DemeterDashboardModel): DemeterDashboardModel => {
        const filteredRows = dragAndDropRows
            .filter((row) => row.widgets.some((widget) => !widget.isAddWidget))
            .map((row) => ({
                name: row.name,
                order: row.order,
                width: row.width,
                height: row.height,
                positionX: row.positionX,
                positionY: row.positionY,
                layoutType: row.layoutType,
                options: row.options,
                rows: row.widgets
                    .filter((widget) => !widget.isAddWidget)
                    .map((widget) => ({
                        demeterDashboardWidgetPageElementGuid: widget.demeterDashboardWidgetPageElementGuid,
                        order: widget.order,
                        options: widget.options,
                        pageElement: widget.pageElement,
                    })),
            }));

        return {
            ...demeterDashboard,
            rows: filteredRows,
        };
    };

    getDashboardDragAndDropRows = (
        dashboardRows: DemeterDashboardWidgetModel[],
        isInEditMode: boolean,
        pageElements: DemeterPageElementModel[],
        handleMoreOptionsSelection: (value: ComponentHeaderRightSideOptions, dashboardWidget: DemeterDashboardWidgetPageElementModel) => void,
        existingRows?: IDragAndDropRow[],
    ) =>
        dashboardRows.map((row, rowIndex) => {
            const existingRow = existingRows?.find((r) => r.order === rowIndex);
            const stableRowId = existingRow?.stableRowId || this.generateStableId('row');

            const rowHasEnforcedLayoutType = pageElementsConstraintsAndInitializations.find((x) =>
                row.rows.some((y) => x.pageElementType === y.pageElement?.pageElementType),
            )?.layoutType;
            const newWidgets = row.rows
                .filter((x) => pageElements.some((y) => y.pageElementType === x.pageElement?.pageElementType))
                .map((widget, widgetIndex) => {
                    const existingWidget = existingRow?.widgets.find(
                        (w) => w.demeterDashboardWidgetPageElementGuid === widget.demeterDashboardWidgetPageElementGuid,
                    );

                    const stableWidgetId = existingWidget?.stableWidgetId || this.generateStableId('widget');

                    const showMaximizeMinimizeButton = isInEditMode && widget.pageElement && !rowHasEnforcedLayoutType;
                    const initialMaximizeMinimizeValue =
                        showMaximizeMinimizeButton && row.layoutType === DemeterDashboardWidgetLayoutType.FullWidth
                            ? ComponentHeaderRightSideOptions.Minimize
                            : ComponentHeaderRightSideOptions.Maximize;
                    const newWidget: IDragAndDropWidget = {
                        ...widget,
                        order: widgetIndex,
                        id: stableWidgetId,
                        stableWidgetId,
                        isAddWidget: false,
                        component: (
                            <GenerateComponentFromPageElement
                                key={stableWidgetId}
                                id={stableWidgetId}
                                dashboardWidget={row}
                                dashboardWidgetPageElement={widget}
                                isInEditMode={isInEditMode}
                                maximizeMinimizeValue={showMaximizeMinimizeButton ? initialMaximizeMinimizeValue : undefined}
                                handleMoreOptionsSelection={handleMoreOptionsSelection}
                            />
                        ),
                    };

                    return newWidget;
                });

            const newRow: IDragAndDropRow = {
                ...row,
                dragAndDropRowId: stableRowId,
                stableRowId,
                order: rowIndex,
                layoutType: rowHasEnforcedLayoutType ?? row.layoutType,
                isRowDivider: existingRow?.isRowDivider || false,
                widgets: newWidgets,
            };

            return newRow;
        });

    getDashboardDragAndDropRowsWithAddWidget = (dashboardRows: IDragAndDropRow[], handleAddWidgetClick: (rowOrder: number, widgetOrder: number) => void) => {
        const emptyRow = {
            name: '',
            width: 0,
            height: 0,
            positionX: 0,
            positionY: 0,
            options: {},
            rows: [],
            widgets: [],
        };

        if (!dashboardRows.length) {
            return [
                this.getRowWithAddWidget(
                    {
                        ...emptyRow,
                        dragAndDropRowId: 'row-0',
                        stableRowId: this.generateStableId('row'),
                        order: 0,
                        isRowDivider: false,
                    },
                    0,
                    handleAddWidgetClick,
                ),
            ];
        }

        const rowsWithAddComponent = dashboardRows.map((row, rowIndex) => {
            const hasAddWidget = row.widgets.some((widget) => widget.isAddWidget);
            const isRowFullWidth = row.layoutType === DemeterDashboardWidgetLayoutType.FullWidth;
            if (!hasAddWidget && !isRowFullWidth) {
                return this.getRowWithAddWidget(row, rowIndex, handleAddWidgetClick);
            }

            return row;
        });

        const lastRow = this.getRowWithAddWidget(
            {
                ...emptyRow,
                dragAndDropRowId: `row-${rowsWithAddComponent.length}`,
                stableRowId: this.generateStableId('row'),
                order: rowsWithAddComponent.length,
                isRowDivider: false,
            },
            rowsWithAddComponent.length,
            handleAddWidgetClick,
        );

        return [...rowsWithAddComponent, lastRow];
    };

    getRowWithAddWidget = (row: IDragAndDropRow, rowOrder: number, handleAddWidgetClick: (rowOrder: number, widgetOrder: number) => void): IDragAndDropRow => {
        const rowWithAddWidgetIDragAndDropRow = {
            name: ' ',
            order: rowOrder,
            dragAndDropRowId: `row-${rowOrder}`,
            stableRowId: row.stableRowId || this.generateStableId('row'),
            width: 0,
            height: 0,
            positionX: 0,
            positionY: 0,
            layoutType: row.layoutType,
            isRowDivider: false,
            options: {},
            rows: [],
            widgets: this.getWidgetsForRowWithAddWidget(row, rowOrder, handleAddWidgetClick),
        };

        return rowWithAddWidgetIDragAndDropRow;
    };

    getWidgetsForRowWithAddWidget = (row: IDragAndDropRow, rowId: number, handleAddWidgetClick: (rowOrder: number, widgetOrder: number) => void) => {
        const numberOfExistingWidgets = row.widgets?.length ?? 0;
        const addWidget = {
            demeterDashboardWidgetPageElementGuid: '',
            isAddWidget: true,
            options: {},
            pageElement: {
                extraParameters: '',
                demeterPageElementGuid: '',
                pageType: DemeterPageType.Dashboard,
                options: {},
            },
        };

        const stableWidgetId = this.generateStableId('add-widget');
        if (!numberOfExistingWidgets) {
            return [
                {
                    ...addWidget,
                    id: stableWidgetId,
                    stableWidgetId,
                    order: numberOfExistingWidgets,
                    component: (
                        <AddWidgetCustomization
                            key={stableWidgetId}
                            handleAddWidgetClick={handleAddWidgetClick}
                            addWidgetRowOrder={rowId}
                            addWidgetWidgetOrder={numberOfExistingWidgets}
                            widgetId={stableWidgetId}
                        />
                    ),
                },
            ];
        }

        if (numberOfExistingWidgets === 1 && row.layoutType !== DemeterDashboardWidgetLayoutType.FullWidth) {
            return [
                ...row.widgets,
                {
                    ...addWidget,
                    id: stableWidgetId,
                    stableWidgetId,
                    order: numberOfExistingWidgets,
                    component: (
                        <AddWidgetCustomization
                            key={stableWidgetId}
                            handleAddWidgetClick={handleAddWidgetClick}
                            addWidgetRowOrder={rowId}
                            addWidgetWidgetOrder={numberOfExistingWidgets}
                            widgetId={stableWidgetId}
                        />
                    ),
                },
            ];
        }

        return row.widgets;
    };

    maximizeWidget = (demeterDashboard: DemeterDashboardModel, dashboardWidget: DemeterDashboardWidgetPageElementModel): DemeterDashboardModel => {
        const currentRowToMaximize = demeterDashboard.rows.find((row) =>
            row.rows.some((widget) => widget.demeterDashboardWidgetPageElementGuid === dashboardWidget.demeterDashboardWidgetPageElementGuid),
        );

        if (!currentRowToMaximize) {
            return demeterDashboard;
        }

        const widgetToMaximize = currentRowToMaximize.rows.find(
            (x) => x.demeterDashboardWidgetPageElementGuid === dashboardWidget.demeterDashboardWidgetPageElementGuid,
        );

        if (!widgetToMaximize) {
            return demeterDashboard;
        }

        const orphanWidget = currentRowToMaximize.rows.find(
            (x) => x.demeterDashboardWidgetPageElementGuid !== widgetToMaximize.demeterDashboardWidgetPageElementGuid,
        );

        widgetToMaximize.order = 0;
        currentRowToMaximize.layoutType = DemeterDashboardWidgetLayoutType.FullWidth;
        currentRowToMaximize.rows = [widgetToMaximize];
        demeterDashboard.rows[currentRowToMaximize.order] = currentRowToMaximize;
        if (orphanWidget) {
            const newRow: DemeterDashboardWidgetModel = {
                name: '',
                order: currentRowToMaximize.order + 1,
                width: 0,
                height: 0,
                positionX: 0,
                positionY: 0,
                layoutType: undefined,
                options: {},
                rows: [
                    {
                        ...orphanWidget,
                        order: 0,
                    },
                ],
            };

            demeterDashboard.rows.splice(currentRowToMaximize.order + 1, 0, newRow);
            demeterDashboard.rows = demeterDashboard.rows.map((row, index) => ({
                ...row,
                order: index,
            }));

            return demeterDashboard;
        }

        return demeterDashboard;
    };

    minimizeWidget = (demeterDashboard: DemeterDashboardModel, dashboardWidget: DemeterDashboardWidgetPageElementModel): DemeterDashboardModel => {
        const rowToMinimize = demeterDashboard.rows.find((row) =>
            row.rows.some((widget) => widget.demeterDashboardWidgetPageElementGuid === dashboardWidget.demeterDashboardWidgetPageElementGuid),
        );

        if (!rowToMinimize) {
            return demeterDashboard;
        }

        rowToMinimize.layoutType = undefined;
        return demeterDashboard;
    };

    cloneWidget = (
        demeterDashboard: DemeterDashboardModel,
        dashboardWidget: DemeterDashboardWidgetPageElementModel,
        temporaryId: number,
    ): DemeterDashboardModel | null => {
        const sourceRowIndex = demeterDashboard.rows.findIndex((row) =>
            row.rows.some((widget) => widget.demeterDashboardWidgetPageElementGuid === dashboardWidget.demeterDashboardWidgetPageElementGuid),
        );

        if (sourceRowIndex === -1) {
            return null;
        }

        const sourceRow = demeterDashboard.rows[sourceRowIndex];
        const widgetIndex = sourceRow.rows.findIndex(
            (widget) => widget.demeterDashboardWidgetPageElementGuid === dashboardWidget.demeterDashboardWidgetPageElementGuid,
        );

        if (widgetIndex === -1) {
            return null;
        }

        const clonedWidget: DemeterDashboardWidgetPageElementModel = {
            ...dashboardWidget,
            demeterDashboardWidgetPageElementGuid: `${temporaryId}`,
            order: widgetIndex + 1,
        };

        if (sourceRow.layoutType === DemeterDashboardWidgetLayoutType.FullWidth) {
            const newRow: DemeterDashboardWidgetModel = {
                name: '',
                order: sourceRowIndex + 1,
                width: 0,
                height: 0,
                positionX: 0,
                positionY: 0,
                layoutType: sourceRow.layoutType,
                options: {},
                rows: [{ ...clonedWidget, order: 0 }],
            };
            demeterDashboard.rows.splice(sourceRowIndex + 1, 0, newRow);
            demeterDashboard.rows = demeterDashboard.rows.map((row, index) => ({
                ...row,
                order: index,
            }));
        } else if (sourceRow.rows.length >= 2) {
            const orphanWidget = !widgetIndex ? sourceRow.rows.splice(widgetIndex + 1, 1, clonedWidget)[0] : clonedWidget;
            const newRow: DemeterDashboardWidgetModel = {
                name: '',
                order: sourceRowIndex + 1,
                width: 0,
                height: 0,
                positionX: 0,
                positionY: 0,
                layoutType: sourceRow.layoutType,
                options: {},
                rows: [{ ...orphanWidget, order: 0 }],
            };
            demeterDashboard.rows.splice(sourceRowIndex + 1, 0, newRow);
            sourceRow.rows = sourceRow.rows.map((widget, index) => ({
                ...widget,
                order: index,
            }));
            demeterDashboard.rows[sourceRowIndex] = sourceRow;
            demeterDashboard.rows = demeterDashboard.rows.map((row, index) => ({
                ...row,
                order: index,
            }));
        } else {
            demeterDashboard.rows[sourceRowIndex].rows.push(clonedWidget);
            demeterDashboard.rows[sourceRowIndex].rows = demeterDashboard.rows[sourceRowIndex].rows.map((widget, index) => ({
                ...widget,
                order: index,
            }));
        }

        return demeterDashboard;
    };

    deleteWidget = (demeterDashboard: DemeterDashboardModel, dashboardWidget: DemeterDashboardWidgetPageElementModel): DemeterDashboardModel => {
        demeterDashboard.rows.forEach((row) => {
            row.rows = row.rows.filter((widget) => widget.demeterDashboardWidgetPageElementGuid !== dashboardWidget.demeterDashboardWidgetPageElementGuid);
            row.rows.forEach((widget, index) => {
                widget.order = index;
            });
        });

        demeterDashboard.rows = demeterDashboard.rows.filter((row) => row.rows.length > 0);
        demeterDashboard.rows.forEach((row, index) => {
            row.order = index;
        });

        return demeterDashboard;
    };

    renameWidget = (
        demeterDashboard: DemeterDashboardModel,
        updateDemeterDashboardWidgetRequest: DemeterDashboardWidgetPageElementModel,
        widgetName: string,
    ): DemeterDashboardModel => {
        const updatedDashboard: DemeterDashboardModel = {
            ...demeterDashboard,
            rows: demeterDashboard.rows.map((row) => ({
                ...row,
                rows: row.rows.map((widget) => {
                    if (widget.demeterDashboardWidgetPageElementGuid === updateDemeterDashboardWidgetRequest.demeterDashboardWidgetPageElementGuid) {
                        return {
                            ...widget,
                            options: {
                                ...widget.options,
                                customTitle: widgetName,
                            },
                        };
                    }
                    return widget;
                }),
            })),
        };

        return updatedDashboard;
    };

    handleWidgetDrop = (dragAndDropRows: IDragAndDropRow[], activeId: UniqueIdentifier, overId: UniqueIdentifier) => {
        const sourceRow = dragAndDropRows.find((row) => row.widgets.some((widget) => widget.id.toString() === activeId));
        const targetRow = dragAndDropRows.find((row) => row.widgets.some((widget) => widget.id.toString() === overId));
        if (!sourceRow || !targetRow) {
            return null;
        }

        const activeWidget = sourceRow.widgets.find((widget) => widget.id.toString() === activeId);
        const targetWidget = targetRow.widgets.find((widget) => widget.id.toString() === overId);
        if (!activeWidget || !targetWidget) {
            return null;
        }

        if (sourceRow.order === targetRow.order) {
            const newWidgets = arraySwap(sourceRow.widgets, activeWidget.order, targetWidget.order).map((widget, index) => ({
                ...widget,
                order: index,
            }));

            return dragAndDropRows.map((row, index) => (index === sourceRow.order ? { ...row, widgets: newWidgets } : row));
        }

        if (sourceRow.layoutType !== DemeterDashboardWidgetLayoutType.FullWidth && targetRow.layoutType !== DemeterDashboardWidgetLayoutType.FullWidth) {
            const newRows = dragAndDropRows.map((row, rowIndex) => {
                if (rowIndex === targetRow.order) {
                    const newWidgets = [...row.widgets];
                    newWidgets[targetWidget.order] = { ...activeWidget, order: targetWidget.order };
                    return { ...row, widgets: newWidgets };
                }

                if (rowIndex === sourceRow.order) {
                    const newWidgets = [...row.widgets];
                    newWidgets[activeWidget.order] = { ...targetWidget, order: activeWidget.order };
                    return { ...row, widgets: newWidgets };
                }

                return row;
            });

            return newRows;
        }

        if (sourceRow.layoutType === DemeterDashboardWidgetLayoutType.FullWidth || targetRow.layoutType === DemeterDashboardWidgetLayoutType.FullWidth) {
            const update = arrayMove(dragAndDropRows, sourceRow.order, targetRow.order).map((row, index) => ({
                ...row,
                order: index,
            }));

            return update;
        }

        return null;
    };

    handleWidgetDropToNewRow = (
        dragAndDropRows: IDragAndDropRow[],
        activeId: UniqueIdentifier,
        targetRowIndex: number,
        handleAddWidgetClick: (rowOrder: number, widgetOrder: number) => void,
    ) => {
        const sourceRow = dragAndDropRows.find((row) => row.widgets.some((widget) => widget.id.toString() === activeId));
        if (!sourceRow) {
            return null;
        }

        const activeWidget = sourceRow.widgets.find((widget) => widget.id.toString() === activeId);
        if (!activeWidget || activeWidget.isAddWidget) {
            return null;
        }

        const updatedSourceRow = {
            ...sourceRow,
            widgets: sourceRow.widgets
                .filter((widget) => widget.id !== activeId)
                .map((widget, index) => ({
                    ...widget,
                    order: index,
                })),
        };

        const newRow = this.getRowWithAddWidget(
            {
                name: '',
                order: targetRowIndex,
                dragAndDropRowId: `row-${targetRowIndex}`,
                stableRowId: this.generateStableId('row'),
                isRowDivider: false,
                width: 0,
                height: 0,
                positionX: 0,
                positionY: 0,
                layoutType: sourceRow.layoutType,
                options: {},
                rows: [],
                widgets: [
                    {
                        ...activeWidget,
                        order: 0,
                    },
                ],
            },
            targetRowIndex,
            handleAddWidgetClick,
        );

        let updatedRows = dragAndDropRows.map((row) => {
            if (row.order === sourceRow.order) {
                return updatedSourceRow;
            }

            return row;
        });

        updatedRows = updatedRows.filter(
            (row) => row.widgets.length > 0 && (row.widgets.some((widget) => !widget.isAddWidget) || row.widgets.some((widget) => widget.isAddWidget)),
        );

        updatedRows.splice(targetRowIndex, 0, newRow);

        return updatedRows.map((row, index) => ({
            ...row,
            order: index,
            dragAndDropRowId: `row-${index}`,
        }));
    };
}

const dashboardCustomizationService = new DashboardCustomizationService();
export default dashboardCustomizationService;
